import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { setCurrentTaxYear } from '../services/TaxYearHeader';

interface SelectedClient {
  id: string;
  name: string;
}

interface SelectedPartnership {
  id: string;
  name: string;
  entityType: string;
}

interface PartnershipContextValue {
  taxYear: string;
  setTaxYear: (year: string) => void;
  selectedClient: SelectedClient | null;
  setSelectedClient: (client: SelectedClient | null) => void;
  selectedPartnership: SelectedPartnership | null;
  setSelectedPartnership: (partnership: SelectedPartnership | null) => void;
  contextInitError: boolean;
}

const PartnershipContext = createContext<PartnershipContextValue | null>(null);

const STORAGE_KEY = 'calcengine_partnership_ctx';

export function PartnershipProvider({ children }: { children: React.ReactNode }) {
  const [taxYear, setTaxYearState] = useState<string>(() => {
    // Read the stored selection up front so the tax-year header (FR-19.8) is right for the
    // very first API calls, which child components fire before this provider's effects run.
    let initial = 'FY2024';
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      const parsed = stored ? JSON.parse(stored) : null;
      if (parsed?.taxYear) initial = parsed.taxYear;
    } catch (err) {
      console.error('[PartnershipContext] Failed to read stored tax year:', err);
    }
    setCurrentTaxYear(initial);
    return initial;
  });
  // Keeps the tax-year header in step with the selection before the next request goes out.
  const setTaxYear = useCallback((year: string) => {
    setCurrentTaxYear(year);
    setTaxYearState(year);
  }, []);
  const [selectedClient, setSelectedClient] = useState<SelectedClient | null>(null);
  const [selectedPartnership, setSelectedPartnership] = useState<SelectedPartnership | null>(null);
  const [contextInitError, setContextInitError] = useState(false);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // taxYear must be restored alongside selectedPartnership — resetting it to the FY2024
        // default while the partnership carries over silently re-resolves screens that key off
        // both (e.g. Generate Tax Form's GET /project/by-partnership?...&taxYear=) against the
        // wrong (or a nonexistent) project on reload (BA-BUG-3, 2026-07-24).
        if (parsed.taxYear) setTaxYear(parsed.taxYear);
        if (parsed.selectedClient) setSelectedClient(parsed.selectedClient);
        if (parsed.selectedPartnership) setSelectedPartnership(parsed.selectedPartnership);
      }
    } catch (err) {
      console.error('[PartnershipContext] Failed to initialize:', err);
      setContextInitError(true);
    }
  }, [setTaxYear]); // setTaxYear is stable (useCallback, no deps) — still runs once on mount

  useEffect(() => {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ taxYear, selectedClient, selectedPartnership })
      );
    } catch (err) {
      console.error('[PartnershipContext] Failed to persist selection:', err);
    }
  }, [taxYear, selectedClient, selectedPartnership]);

  return (
    <PartnershipContext.Provider
      value={{
        taxYear, setTaxYear,
        selectedClient, setSelectedClient,
        selectedPartnership, setSelectedPartnership,
        contextInitError,
      }}
    >
      {children}
    </PartnershipContext.Provider>
  );
}

export function usePartnershipContext(): PartnershipContextValue {
  const ctx = useContext(PartnershipContext);
  if (!ctx) throw new Error('usePartnershipContext must be used within PartnershipProvider');
  return ctx;
}
